// src/components/AuthModal.js
import React, { useState } from 'react';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider, 
  signInWithPopup 
} from 'firebase/auth';

const styles = `
  /* ── Modal Auth ── */
  .vedo-auth-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(6px);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .vedo-auth-modal {
    background: #0f1e35;
    border: 1px solid rgba(0,173,181,0.22);
    border-radius: 14px;
    padding: 36px 32px 28px;
    width: 100%;
    max-width: 380px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6);
    animation: modalIn 0.25s cubic-bezier(0.4,0,0.2,1);
    position: relative;
    color: #f0f4f8;
    font-family: 'DM Sans', sans-serif;
  }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .vedo-auth-modal__close {
    position: absolute;
    top: 14px; right: 16px;
    background: none; border: none;
    color: rgba(240,244,248,0.45); cursor: pointer;
    font-size: 20px; line-height: 1; padding: 4px;
    transition: color 0.22s;
  }
  .vedo-auth-modal__close:hover { color: #f0f4f8; }
  .vedo-auth-modal__logo { text-align: center; margin-bottom: 6px; }
  .vedo-auth-modal__logo span {
    font-family: 'DM Mono', monospace;
    font-size: 11px; letter-spacing: 0.25em;
    text-transform: uppercase; color: #00adb5;
  }
  .vedo-auth-modal__title {
    font-size: 18px; font-weight: 600;
    color: #f0f4f8; text-align: center; margin-bottom: 4px;
  }
  .vedo-auth-modal__sub {
    font-size: 12.5px; color: rgba(240,244,248,0.45);
    text-align: center; margin-bottom: 24px; line-height: 1.5;
  }
  .vedo-auth-tabs {
    display: flex;
    border-bottom: 1px solid rgba(0,173,181,0.22);
    margin-bottom: 22px;
  }
  .vedo-auth-tab {
    flex: 1; background: none; border: none;
    padding: 8px 0 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 500; color: rgba(240,244,248,0.45);
    cursor: pointer; border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color 0.22s, border-color 0.22s;
  }
  .vedo-auth-tab.active { color: #00adb5; border-bottom-color: #00adb5; }
  .vedo-auth-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .vedo-auth-field label {
    font-size: 11.5px; font-weight: 500;
    color: rgba(240,244,248,0.45); letter-spacing: 0.06em; text-transform: uppercase;
  }
  .vedo-auth-field input {
    background: rgba(0,173,181,0.12); border: 1px solid rgba(0,173,181,0.22);
    border-radius: 7px; padding: 10px 13px;
    font-family: 'DM Sans', sans-serif; font-size: 14px; color: #f0f4f8;
    outline: none; transition: border-color 0.22s, background 0.22s;
  }
  .vedo-auth-field input::placeholder { color: rgba(240,244,248,0.45); }
  .vedo-auth-field input:focus { border-color: #00adb5; background: rgba(0,173,181,0.14); }
  .vedo-auth-error {
    background: rgba(255,87,34,0.1); border: 1px solid rgba(255,87,34,0.3);
    border-radius: 6px; padding: 9px 12px;
    font-size: 12.5px; color: #ff7043; margin-bottom: 14px; line-height: 1.4;
  }
  .vedo-auth-submit {
    width: 100%; background: #00adb5; border: none; border-radius: 7px;
    color: #fff; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; padding: 11px; cursor: pointer;
    letter-spacing: 0.03em;
    transition: background 0.22s, box-shadow 0.22s, transform 0.22s;
    margin-top: 4px;
  }
  .vedo-auth-submit:hover { background: #009aa2; box-shadow: 0 4px 16px rgba(0,173,181,0.4); transform: translateY(-1px); }
  .vedo-auth-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  .vedo-auth-divider {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0; color: rgba(240,244,248,0.45); font-size: 11px; letter-spacing: 0.1em;
  }
  .vedo-auth-divider::before, .vedo-auth-divider::after {
    content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.07);
  }
  .vedo-auth-google {
    width: 100%; background: transparent; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 7px; color: #f0f4f8;
    font-family: 'DM Sans', sans-serif; font-size: 13.5px; font-weight: 400;
    padding: 10px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 9px;
    transition: background 0.22s, border-color 0.22s;
  }
  .vedo-auth-google:hover { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.22); }
`;

const AuthModal = ({ onClose, onSuccess }) => {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const auth = getAuth();

  const friendlyError = (code) => ({
    'auth/invalid-email': 'E-mail inválido.',
    'auth/user-not-found': 'Usuário não encontrado.',
    'auth/wrong-password': 'Senha incorreta.',
    'auth/email-already-in-use': 'Este e-mail já está cadastrado.',
    'auth/weak-password': 'Senha muito fraca. Use ao menos 6 caracteres.',
    'auth/too-many-requests': 'Muitas tentativas. Tente novamente em breve.',
    'auth/popup-closed-by-user': 'Login cancelado.',
  }[code] || 'Ocorreu um erro. Tente novamente.');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (tab === 'login') await signInWithEmailAndPassword(auth, email, password);
      else await createUserWithEmailAndPassword(auth, email, password);
      onSuccess();
    } catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setError(''); setLoading(true);
    try { 
      await signInWithPopup(auth, new GoogleAuthProvider()); 
      onSuccess(); 
    }
    catch (err) { setError(friendlyError(err.code)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="vedo-auth-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="vedo-auth-modal">
          <button className="vedo-auth-modal__close" onClick={onClose}>×</button>
          <div className="vedo-auth-modal__logo"><span>VEDO</span></div>
          <h2 className="vedo-auth-modal__title">
            {tab === 'login' ? 'Bem-vindo de volta' : 'Criar conta'}
          </h2>
          <p className="vedo-auth-modal__sub">
            {tab === 'login'
              ? 'Entre para visualizar os preços dos domínios.'
              : 'Cadastre-se gratuitamente para ver os preços.'}
          </p>
          <div className="vedo-auth-tabs">
            <button className={`vedo-auth-tab${tab === 'login' ? ' active' : ''}`} onClick={() => { setTab('login'); setError(''); }}>Entrar</button>
            <button className={`vedo-auth-tab${tab === 'register' ? ' active' : ''}`} onClick={() => { setTab('register'); setError(''); }}>Cadastrar</button>
          </div>
          <form onSubmit={handleEmailAuth}>
            {error && <div className="vedo-auth-error">{error}</div>}
            <div className="vedo-auth-field">
              <label>E-mail</label>
              <input type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="vedo-auth-field">
              <label>Senha</label>
              <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button type="submit" className="vedo-auth-submit" disabled={loading}>
              {loading ? 'Aguarde...' : tab === 'login' ? 'Entrar' : 'Criar conta'}
            </button>
          </form>
          <div className="vedo-auth-divider">ou</div>
          <button className="vedo-auth-google" onClick={handleGoogle} disabled={loading}>
            {/* SVG do Google... */}
            Continuar com Google
          </button>
        </div>
      </div>
    </>
  );
};

export default AuthModal;
